import { getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod/v4'
import { invariantResponse } from '@epic-web/invariant'
import * as cookie from 'cookie'
import {
	type ActionFunctionArgs,
	data,
	redirect,
	useFetcher,
	useFetchers,
} from 'react-router'
import { ServerOnly } from 'remix-utils/server-only'
import { z } from 'zod'
import { Icon } from '#app/components/ui/icon.tsx'
import { useHints } from '#app/utils/client-hints.tsx'
import { type CookiePreferences } from '#app/utils/cookie-consent.ts'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { setTheme, type Theme } from '#app/utils/theme.server.ts'

const ThemeFormSchema = z.object({
	theme: z.enum(['system', 'light', 'dark']),
	redirectTo: z.string().optional(),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid theme received')

	const { theme, redirectTo } = submission.value

	const cookieHeader = request.headers.get('cookie')
	let preferencesAllowed = false

	if (cookieHeader) {
		const consentCookie =
			cookie.parseCookie(cookieHeader)['tv-cookie-consent'] || '{}'

		if (consentCookie) {
			try {
				const { preferences } = JSON.parse(
					decodeURIComponent(consentCookie),
				) as CookiePreferences
				preferencesAllowed = preferences
			} catch {
				preferencesAllowed = false
			}
		}
	}
	const responseInit: Record<string, string> = {}
	if (preferencesAllowed) {
		responseInit['Set-Cookie'] = setTheme(theme)
	}

	if (redirectTo) {
		return redirect(redirectTo, { headers: responseInit })
	} else {
		return data({ result: submission.reply() }, { headers: responseInit })
	}
}

export function ThemeSwitch({
	userPreference,
}: {
	userPreference?: Theme | null
}) {
	const fetcher = useFetcher<typeof action>()
	const requestInfo = useRequestInfo()

	const [form] = useForm({
		id: 'theme-switch',
		lastResult: fetcher.data?.result,
	})

	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? userPreference ?? 'system'
	const nextMode =
		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	const modeLabel = {
		light: (
			<Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>
		),
		dark: (
			<Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>
		),
		system: (
			<Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>
		),
	}

	const preferenceCookiesAllowed =
		requestInfo.userPrefs.cookieConsent.preferences

	if (preferenceCookiesAllowed) {
		return (
			<fetcher.Form
				method="POST"
				{...getFormProps(form)}
				action="/resources/theme-switch"
			>
				<ServerOnly>
					{() => (
						<input type="hidden" name="redirectTo" value={requestInfo.path} />
					)}
				</ServerOnly>
				<input type="hidden" name="theme" value={nextMode} />
				<div className="flex gap-2">
					<button
						type="submit"
						className="flex h-8 w-8 cursor-pointer items-center justify-center"
					>
						{modeLabel[mode]}
					</button>
				</div>
			</fetcher.Form>
		)
	}

	return null
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
function useOptimisticThemeMode() {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(
		(f) => f.formAction === '/resources/theme-switch',
	)

	if (themeFetcher && themeFetcher.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	return requestInfo.userPrefs.theme ?? hints.theme
}
